/**
 * Client calls of the shop file's DATEV tab (work order item 8).
 *
 * Built on `opsClient()` / `shopGrant()` from `ops-api.ts`: every call carries the shop's access
 * grant, and a refusal for want of one is recognised there (`isShopAccessRequired`), not here.
 * Two writes, both resends — support may do nothing else to a merchant's Steuerberater setup
 * (AC-8.4) — and each takes the operator's reason.
 */

import type {
  OpsDatevInviteResendResultDto,
  OpsDatevOverviewDto,
  OpsRecipientResendResultDto,
} from '../../../../libs/api-client/src/generated/models';
import { opsClient, shopGrant } from './ops-api';

// Re-exported from the generated client rather than restated here — see `support-api.ts`.
export type {
  OpsDatevExportJobDto as OpsDatevExportJob,
  OpsDatevInviteDto as OpsDatevInvite,
  OpsDatevInviteResendResultDto as OpsDatevInviteResendResult,
  OpsDatevOverviewDto as OpsDatevOverview,
  OpsDatevSettingsSummaryDto as OpsDatevSettingsSummary,
  OpsExportScheduleDto as OpsExportSchedule,
  OpsRecipientResendResultDto as OpsRecipientResendResult,
  OpsScheduleRecipientDto as OpsScheduleRecipient,
} from '../../../../libs/api-client/src/generated/models';

/** The DATEV tab in one read: settings + lock, invites, schedules with recipients, exports. */
export async function fetchShopDatev(
  shopId: string,
): Promise<OpsDatevOverviewDto> {
  const { data } = await opsClient().getOpsShopDatev({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
  });
  return data;
}

/**
 * Resends the shop's pending Steuerberater invite. The Kanzlei's previous link stops working.
 *
 * @param reason - The operator's reason, stored in `OperatorAuditLog`.
 */
export async function resendDatevInvite(
  shopId: string,
  inviteId: string,
  reason: string,
): Promise<OpsDatevInviteResendResultDto> {
  const { data } = await opsClient().resendOpsDatevInvite({
    shopId,
    inviteId,
    xOpsShopAccess: shopGrant(shopId),
    operatorReasonDto: { reason },
  });
  return data;
}

/**
 * Resends a pending export recipient's confirmation email. Their earlier link stops working.
 *
 * @param reason - The operator's reason, stored in `OperatorAuditLog`.
 */
export async function resendRecipientConfirmation(
  shopId: string,
  recipientId: string,
  reason: string,
): Promise<OpsRecipientResendResultDto> {
  const { data } = await opsClient().resendOpsRecipientConfirmation({
    shopId,
    recipientId,
    xOpsShopAccess: shopGrant(shopId),
    operatorReasonDto: { reason },
  });
  return data;
}
